import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslations } from './hooks/useTranslations';
import BrandValueMethodologyModal from './components/BrandValueMethodologyModal';

const FAQPage = () => {
  const { t } = useTranslations();
  const [openFAQ, setOpenFAQ] = useState(null);
  const [isMethodologyModalOpen, setIsMethodologyModalOpen] = useState(false);

  // Forzar scroll al inicio cuando se carga la página
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  // Consolidamos todas las FAQs desde diferentes secciones
  const allFAQs = [
    // FAQs generales del home
    {
      question: t('home.faq.questions.q1.question'),
      answer: t('home.faq.questions.q1.answer'),
      category: t('faqPage.categories.valuation')
    },
    {
      question: t('home.faq.questions.q2.question'),
      answer: t('home.faq.questions.q2.answer'),
      category: t('faqPage.categories.valuation')
    },
    {
      question: t('home.faq.questions.q3.question'),
      answer: t('home.faq.questions.q3.answer'),
      category: t('faqPage.categories.valuation')
    },
    {
      question: t('home.faq.questions.q4.question'),
      answer: t('home.faq.questions.q4.answer'),
      category: t('faqPage.categories.valuation')
    },
    {
      question: t('home.faq.questions.q5.question'),
      answer: t('home.faq.questions.q5.answer'),
      category: t('faqPage.categories.security')
    },
    // Nuevas FAQs sobre Brand Equity vs Brand Value
    {
      question: t('home.faq.questions.q6.question'),
      answer: t('home.faq.questions.q6.answer'),
      category: t('faqPage.categories.valuation')
    },
    {
      question: t('home.faq.questions.q7.question'),
      answer: t('home.faq.questions.q7.answer'),
      category: t('faqPage.categories.valuation')
    },
    {
      question: t('home.faq.questions.q8.question'),
      answer: t('home.faq.questions.q8.answer'),
      category: t('faqPage.categories.valuation')
    },
    // FAQs de precios
    {
      question: t('pricing.faq.q1.question'),
      answer: t('pricing.faq.q1.answer'),
      category: t('faqPage.categories.pricing')
    },
    {
      question: t('pricing.faq.q2.question'),
      answer: t('pricing.faq.q2.answer'),
      category: t('faqPage.categories.pricing')
    },
    {
      question: t('pricing.faq.q3.question'),
      answer: t('pricing.faq.q3.answer'),
      category: t('faqPage.categories.pricing')
    },
    {
      question: t('pricing.faq.q4.question'),
      answer: t('pricing.faq.q4.answer'),
      category: t('faqPage.categories.pricing')
    },
    // FAQs de asesoría
    {
      question: t('advisory.faq.questions.q1.question'),
      answer: t('advisory.faq.questions.q1.answer'),
      category: t('faqPage.categories.advisory')
    },
    {
      question: t('advisory.faq.questions.q2.question'),
      answer: t('advisory.faq.questions.q2.answer'),
      category: t('faqPage.categories.advisory')
    },
    {
      question: t('advisory.faq.questions.q3.question'),
      answer: t('advisory.faq.questions.q3.answer'),
      category: t('faqPage.categories.advisory')
    },
    {
      question: t('advisory.faq.questions.q4.question'),
      answer: t('advisory.faq.questions.q4.answer'),
      category: t('faqPage.categories.advisory')
    },
    {
      question: t('advisory.faq.questions.q5.question'),
      answer: t('advisory.faq.questions.q5.answer'),
      category: t('faqPage.categories.advisory')
    },
    {
      question: t('advisory.faq.questions.q6.question'),
      answer: t('advisory.faq.questions.q6.answer'),
      category: t('faqPage.categories.advisory')
    }
  ];

  // Agrupar FAQs por categoría
  const categories = [...new Set(allFAQs.map(faq => faq.category))];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
        {/* Back Button */}
        <Link 
          to="/" 
          className="inline-flex items-center text-purple-400 hover:text-purple-300 transition-colors duration-300 mb-8"
        >
          <span className="mr-2">←</span>
          {t('home.calculator.backHome')}
        </Link>

        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-white mb-6">
            {t('faqPage.title')}
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            {t('faqPage.subtitle')}
          </p>
          
          {/* Methodology Button */}
          <button
            onClick={() => setIsMethodologyModalOpen(true)}
            className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-cyan-700 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 shadow-lg inline-flex items-center"
          >
            <span className="mr-2">🧮</span>
            {t('methodology.title')}
          </button>
        </div>

        {/* FAQ Categories */}
        {categories.map((category, categoryIndex) => (
          <div key={categoryIndex} className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-6 border-b border-purple-500/30 pb-3">
              {category}
            </h2>
            
            <div className="space-y-4">
              {allFAQs
                .filter(faq => faq.category === category)
                .map((faq, index) => {
                  const globalIndex = categoryIndex * 100 + index; // Unique index across categories
                  return (
                    <div 
                      key={globalIndex} 
                      className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-purple-500/20 overflow-hidden hover:border-purple-500/40 transition-all duration-300"
                    >
                      <button
                        onClick={() => toggleFAQ(globalIndex)}
                        className="w-full p-6 text-left flex justify-between items-center hover:bg-purple-500/10 transition-colors duration-300"
                      >
                        <span className="font-semibold text-white pr-4">
                          {faq.question}
                        </span>
                        <span className={`text-purple-400 text-xl transition-transform duration-300 flex-shrink-0 ${
                          openFAQ === globalIndex ? 'rotate-180' : ''
                        }`}>
                          ↓
                        </span>
                      </button>
                      
                      <div className={`transition-all duration-300 ${
                        openFAQ === globalIndex 
                          ? 'max-h-96 opacity-100 pb-6 px-6' 
                          : 'max-h-0 opacity-0'
                      } overflow-hidden`}>
                        <p className="text-gray-300 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  );
                })
              }
            </div>
          </div>
        ))}

        {/* Contact CTA */}
        <div className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 rounded-2xl p-8 border border-purple-500/30 backdrop-blur-sm text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            {t('faqPage.contactCta.title')}
          </h2>
          <p className="text-gray-300 mb-6">
            {t('faqPage.contactCta.subtitle')}
          </p>
          <div className="flex justify-center">
            <a
              href="https://calendly.com/tamaranavarretenavas/asesoria-your-brand-value"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              {t('faqPage.contactCta.sessionButton')}
            </a>
          </div>
        </div>

        {/* Methodology Modal */}
        <BrandValueMethodologyModal 
          isOpen={isMethodologyModalOpen}
          onClose={() => setIsMethodologyModalOpen(false)}
        />
      </div>
    </div>
  );
};

export default FAQPage;